import Api from '@/data/Api.ts';
import type { PerformanceRatioJson } from '@/data/yields/PerformanceRatioJson.ts';
import StringUtils from '@/infrastructure/support/utils/StringUtils.ts';
import type Company from '@/domain/model/business/Company.ts';
import type Plant from '@/domain/model/business/Plant.ts';
import type Meter from '@/domain/model/business/Meter.ts';
import type PeriodType from '@/infrastructure/support/PeriodType.ts';

const COMPANY_YIELD = '/companies/{0}/yield';
const PLANT_YIELD = '/companies/{0}/plants/{1}/yield';
const METER_YIELD = '/companies/{0}/plants/{1}/meters/{2}/yield';

export default class YieldApi extends Api {
  companyYield(
    company: Company,
    dateStart: string,
    dateEnd: string,
    periodType: PeriodType,
    yieldType: string,
  ): Promise<any> {
    return this.standardYieldGet(
      StringUtils.stringFormat(COMPANY_YIELD, [company.getId()]),
      dateStart,
      dateEnd,
      periodType,
      yieldType,
    );
  }

  plantYield(
    company: Company,
    plant: Plant,
    dateStart: string,
    dateEnd: string,
    periodType: PeriodType,
    yieldType: string,
  ): Promise<any> {
    return this.standardYieldGet(
      StringUtils.stringFormat(PLANT_YIELD, [company.getId(), plant.getId()]),
      dateStart,
      dateEnd,
      periodType,
      yieldType,
    );
  }

  meterYield(
    company: Company,
    plant: Plant,
    meter: Meter,
    dateStart: string,
    dateEnd: string,
    periodType: PeriodType,
    yieldType: string,
  ): Promise<any> {
    return this.standardYieldGet(
      StringUtils.stringFormat(METER_YIELD, [
        company.getId(),
        plant.getId(),
        meter.getId(),
      ]),
      dateStart,
      dateEnd,
      periodType,
      yieldType,
    );
  }

  private standardYieldGet(
    url: string,
    dateStart: string,
    dateEnd: string,
    periodType: PeriodType,
    yieldType: string,
  ): Promise<any> {
    return this.standardGet(
      url,
      {
        start_date: dateStart,
        end_date: dateEnd,
        period_type: periodType,
        yield_type: yieldType,
      },
      [],
    ).then((rsp) => this.extractYields(rsp));
  }

  private extractYields(rsp: any): Array<any> {
    const body = rsp?.data;
    if (Array.isArray(body)) {
      return body;
    }
    return Array.isArray(body?.data) ? body.data : [];
  }

  public plantPerformanceRatios(
    company: Company,
    plant: Plant,
    dateStart: string,
    dateEnd: string,
    periodType: PeriodType,
  ): Promise<any> {
    let yieldPromises = ['actual-yield', 'predicted-yield'] //
      .map((yieldType) =>
        this.standardYieldGet(
          StringUtils.stringFormat(PLANT_YIELD, [
            company.getId(),
            plant.getId(),
          ]),
          dateStart,
          dateEnd,
          periodType,
          yieldType,
        ),
      );

    return Promise.all(yieldPromises).then((results) =>
      this.mapYieldsToPerformanceRatios(results[0], results[1]),
    );
  }

  public meterOrPlantPerformanceRatios(
    company: Company,
    plant: Plant,
    meter: Meter | null,
    dateStart: string,
    dateEnd: string,
    periodType: PeriodType,
  ): Promise<any> {
    const url = meter
      ? StringUtils.stringFormat(METER_YIELD, [
          company.getId(),
          plant.getId(),
          meter.getId(),
        ])
      : StringUtils.stringFormat(PLANT_YIELD, [company.getId(), plant.getId()]);

    let yieldPromises = ['actual-yield', 'predicted-yield'] //
      .map((yieldType) =>
        this.standardYieldGet(url, dateStart, dateEnd, periodType, yieldType),
      );

    return Promise.all(yieldPromises).then((results) =>
      this.mapYieldsToPerformanceRatios(results[0], results[1]),
    );
  }

  private mapYieldsToPerformanceRatios(
    ays: Array<any>,
    pys: Array<any>,
  ): PerformanceRatioJson[] {
    let pysDict = pys.reduce((carry, pYield) => {
      carry[pYield.time] = pYield.yield_in_wh;
      return carry;
    }, {});

    return ays.map(
      (aYield) => ({
        time: new Date(aYield.time),
        performance_ratio: aYield.yield_in_wh / pysDict[aYield.time],
        actual_yield_in_wh: aYield.yield_in_wh,
        predicted_yield_in_wh: pysDict[aYield.time],
      }),
      {},
    );
  }
}
